import { googleSheetsService } from '../google-sheets-service';
import { GOOGLE_CONFIG } from '../app-config';
import type { ReferenceEntry, ReferenceOperationResult } from '../types';

/**
 * Reference Sheet Manager Service
 * Manages references stored in Google Sheets
 */
class ReferenceSheetManagerService {
  private static instance: ReferenceSheetManagerService;

  // Cache for reference data
  private dataCache: ReferenceEntry[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes

  private constructor() {}

  public static getInstance(): ReferenceSheetManagerService {
    if (!ReferenceSheetManagerService.instance) {
      ReferenceSheetManagerService.instance = new ReferenceSheetManagerService();
    }
    return ReferenceSheetManagerService.instance;
  }

  /**
   * Get spreadsheet configuration from app-config
   */
  private getSpreadsheetConfig(): { spreadsheetId: string; sheetName: string; columns: { referenceTag: string; reference: string } } | null {
    const masterSpreadsheetId = (GOOGLE_CONFIG as any).masterSpreadsheet;
    const config = (GOOGLE_CONFIG as any).referencesTable;
    if (!masterSpreadsheetId || !config?.sheetName) {
      console.error('[ReferenceSheetManager] No reference sheet configured in app-config');
      return null;
    }
    return {
      spreadsheetId: masterSpreadsheetId,
      sheetName: config.sheetName,
      columns: config.columns
    };
  }

  /**
   * Check if cache is valid
   */
  private isCacheValid(): boolean {
    return this.dataCache !== null && (Date.now() - this.cacheTimestamp < this.CACHE_TTL);
  }

  /**
   * Find column index by name (case-insensitive)
   */
  private findColumnIndex(headers: string[], name: string): number {
    const normalized = name.toLowerCase().trim();
    for (let i = 0; i < headers.length; i++) {
      if (headers[i]?.toLowerCase().trim() === normalized) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Generate a reference tag from reference text using heuristic parsing.
   * Attempts to infer an [Author Year] style tag and falls back gracefully.
   */
  public generateReferenceTag(referenceText: string): string {
    const normalized = referenceText.replace(/\s+/g, ' ').trim();

    if (!normalized) {
      return `[Ref ${Date.now().toString().slice(-8)}]`;
    }

    const IGNORE_WORDS = new Set(['a', 'an', 'the']);
    const sanitizeName = (input: string) =>
      input.replace(/[^A-Za-z'\u2019\-]/g, '').trim();

    const toCapitalized = (input: string) => {
      if (!input) {
        return '';
      }
      const lower = input.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    };

    const yearMatch = normalized.match(/\b(1[89]\d{2}|20\d{2}|21\d{2})([a-z])?\b/i);
    const year = yearMatch ? yearMatch[0] : null;

    let primaryAuthor: string | null = null;
    let secondaryAuthor: string | null = null;

    const multiAuthorMatch = normalized.match(/^([A-Z][A-Za-z'\u2019\-]+)[^()]*?(?:&|and)\s+([A-Z][A-Za-z'\u2019\-]+)/);
    if (multiAuthorMatch) {
      primaryAuthor = sanitizeName(multiAuthorMatch[1]);
      secondaryAuthor = sanitizeName(multiAuthorMatch[2]);
    }

    if (!primaryAuthor) {
      const apaMatch = normalized.match(/^([A-Z][A-Za-z'\u2019\-]+),\s*[A-Z]/);
      if (apaMatch) {
        primaryAuthor = sanitizeName(apaMatch[1]);
      }
    }

    if (!primaryAuthor) {
      const chicagoMatch = normalized.match(/^([A-Z][A-Za-z'\u2019\-]+)\s+(?:[A-Z]\.|[A-Z][a-z]+)/);
      if (chicagoMatch) {
        primaryAuthor = sanitizeName(chicagoMatch[1]);
      }
    }

    if (!primaryAuthor) {
      const defaultMatch = normalized.match(/\b([A-Z][A-Za-z'\u2019\-]+)\b/);
      if (defaultMatch) {
        const candidate = sanitizeName(defaultMatch[1]);
        if (candidate && !IGNORE_WORDS.has(candidate.toLowerCase())) {
          primaryAuthor = candidate;
        }
      }
    }

    if (primaryAuthor) {
      primaryAuthor = toCapitalized(primaryAuthor);
    }
    if (secondaryAuthor) {
      secondaryAuthor = toCapitalized(secondaryAuthor);
      if (primaryAuthor === secondaryAuthor) {
        secondaryAuthor = null;
      }
    }

    const usesEtAl = /et al\./i.test(normalized);

    if (primaryAuthor && year) {
      if (usesEtAl) {
        return `[${primaryAuthor} et al. ${year}]`;
      }
      if (secondaryAuthor) {
        return `[${primaryAuthor} & ${secondaryAuthor} ${year}]`;
      }
      return `[${primaryAuthor} ${year}]`;
    }

    if (primaryAuthor) {
      return `[${primaryAuthor} ${year ?? 'n.d.'}]`;
    }

    if (year) {
      return `[Unknown ${year}]`;
    }

    const firstWord = normalized.split(/\s+/)[0];
    if (firstWord && firstWord.length > 2) {
      return `[${firstWord.substring(0, 20)}]`;
    }

    return `[Ref ${Date.now().toString(36).slice(-6).toUpperCase()}]`;
  }

  /**
   * Ensures auto-generated tags do not collide with existing entries.
   */
  private makeUniqueTag(baseTag: string, existingRefs: ReferenceEntry[]): string {
    const trimmed = baseTag.trim();
    if (!trimmed) {
      return `[Ref ${Date.now().toString().slice(-8)}]`;
    }

    const existing = new Set(existingRefs.map(ref => ref.tag.trim().toLowerCase()));
    if (!existing.has(trimmed.toLowerCase())) {
      return trimmed;
    }

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      const inner = trimmed.slice(1, -1).trim();
      const match = inner.match(/^(.*?)(1[89]\d{2}|20\d{2}|21\d{2})([a-z])?$/i);
      if (match) {
        const rawPrefix = match[1].trimEnd();
        const year = match[2];
        const suffixes = new Set<string>();
        for (const ref of existingRefs) {
          const candidate = ref.tag.trim();
          if (!candidate.startsWith('[') || !candidate.endsWith(']')) {
            continue;
          }
          const candidateInner = candidate.slice(1, -1).trim();
          const candidateMatch = candidateInner.match(/^(.*?)(1[89]\d{2}|20\d{2}|21\d{2})([a-z])?$/i);
          if (candidateMatch && candidateMatch[1].trimEnd() === rawPrefix && candidateMatch[2] === year) {
            suffixes.add((candidateMatch[3] || '').toLowerCase());
          }
        }

        const alphabet = 'abcdefghijklmnopqrstuvwxyz';
        for (const char of alphabet) {
          if (!suffixes.has(char)) {
            const prefixSegment = rawPrefix ? `${rawPrefix} ` : '';
            const candidateTag = `[${prefixSegment}${year}${char}]`;
            if (!existing.has(candidateTag.toLowerCase())) {
              return candidateTag;
            }
          }
        }
      }
    }

    const baseCore = trimmed.endsWith(']') ? trimmed.slice(0, -1) : trimmed;
    const closing = trimmed.endsWith(']') ? ']' : '';
    let counter = 2;
    let candidate = `${baseCore}-${counter}${closing}`;
    while (existing.has(candidate.toLowerCase())) {
      counter += 1;
      candidate = `${baseCore}-${counter}${closing}`;
    }
    return candidate;
  }

  /**
   * Fetch all reference entries from Google Sheet
   */
  public async getAllReferences(forceRefresh = false): Promise<ReferenceEntry[]> {
    // Check cache
    if (!forceRefresh && this.isCacheValid()) {
      console.log('[ReferenceSheetManager] Returning cached reference data');
      return this.dataCache!;
    }

    const config = this.getSpreadsheetConfig();
    if (!config) {
      throw new Error('Reference sheet not configured. Please add referencesTable to app-config.ts');
    }

    try {
      console.log('[ReferenceSheetManager] Fetching references from Google Sheet:', config.sheetName);

      // Fetch from Google Sheets
      const values = await googleSheetsService.getSheetValues(
        config.spreadsheetId,
        config.sheetName
      );

      if (!values || values.length === 0) {
        console.warn('[ReferenceSheetManager] Reference sheet is empty');
        this.dataCache = [];
        this.cacheTimestamp = Date.now();
        return [];
      }

      // Parse data (first row is header)
      const headers = values[0];
      const rows = values.slice(1);

      // Find column indices
      const tagCol = this.findColumnIndex(headers, config.columns.referenceTag);
      const refCol = this.findColumnIndex(headers, config.columns.reference);

      if (tagCol === -1 || refCol === -1) {
        throw new Error(`Required columns not found. Expected: "${config.columns.referenceTag}" and "${config.columns.reference}"`);
      }

      // Convert to ReferenceEntry objects
      const entries: ReferenceEntry[] = rows
        .filter(row => row[tagCol]?.trim()) // Only rows with valid tag
        .map((row, index) => ({
          tag: row[tagCol].trim(),
          reference: row[refCol] || '',
          rowIndex: index + 1 // +1 because we sliced off header
        }));

      // Update cache
      this.dataCache = entries;
      this.cacheTimestamp = Date.now();

      console.log(`[ReferenceSheetManager] Loaded ${entries.length} reference entries`);
      return entries;

    } catch (error) {
      console.error('[ReferenceSheetManager] Failed to fetch reference data:', error);
      throw error;
    }
  }

  /**
   * Add new reference entry (append row to sheet)
   */
  public async addReference(referenceText: string, customTag?: string): Promise<ReferenceOperationResult> {
    const config = this.getSpreadsheetConfig();
    if (!config) {
      return {
        success: false,
        affectedCount: 0,
        error: 'Reference sheet not configured'
      };
    }

    try {
      const existingRefs = await this.getAllReferences();
      const isCustomTag = !!customTag?.trim();
      let tag = (customTag?.trim() || this.generateReferenceTag(referenceText)).trim();

      if (isCustomTag) {
        if (existingRefs.some(ref => ref.tag === tag)) {
          return {
            success: false,
            affectedCount: 0,
            error: `Reference tag "${tag}" already exists. Please use a unique tag.`
          };
        }
      } else {
        tag = this.makeUniqueTag(tag, existingRefs);
      }

      console.log(`[ReferenceSheetManager] Adding new reference with tag: ${tag}`);

      // Append new row
      await googleSheetsService.appendRows(
        config.spreadsheetId,
        config.sheetName,
        [[tag, referenceText]]
      );

      // Clear cache to force refresh
      this.clearCache();

      console.log(`[ReferenceSheetManager] Successfully added reference: ${tag}`);

      return {
        success: true,
        affectedCount: 1,
        tag,
        details: `Added reference with tag: ${tag}`
      };

    } catch (error) {
      console.error('[ReferenceSheetManager] Failed to add reference:', error);
      return {
        success: false,
        affectedCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Find a reference by tag
   */
  public async getReferenceByTag(tag: string): Promise<ReferenceEntry | null> {
    const allRefs = await this.getAllReferences();
    return allRefs.find(ref => ref.tag === tag) || null;
  }

  /**
   * Search references by text (simple contains search)
   */
  public async searchReferences(searchTerm: string): Promise<ReferenceEntry[]> {
    const allRefs = await this.getAllReferences();
    const term = searchTerm.toLowerCase();

    return allRefs.filter(ref =>
      ref.tag.toLowerCase().includes(term) ||
      ref.reference.toLowerCase().includes(term)
    );
  }

  /**
   * Delete a reference by tag
   */
  public async deleteReference(tag: string): Promise<ReferenceOperationResult> {
    const config = this.getSpreadsheetConfig();
    if (!config) {
      return {
        success: false,
        affectedCount: 0,
        error: 'Reference sheet not configured'
      };
    }

    try {
      // Get all references to find the one to delete
      const allRefs = await this.getAllReferences();
      const refToDelete = allRefs.find(ref => ref.tag === tag);

      if (!refToDelete) {
        return {
          success: false,
          affectedCount: 0,
          error: `Reference with tag "${tag}" not found`
        };
      }

      if (refToDelete.rowIndex === undefined) {
        return {
          success: false,
          affectedCount: 0,
          error: 'Reference row index not available'
        };
      }

      console.log(`[ReferenceSheetManager] Deleting reference: ${tag} at row ${refToDelete.rowIndex}`);

      // Delete the row (rowIndex is 0-based relative to data rows, need to add 1 for header)
      await googleSheetsService.deleteRow(
        config.spreadsheetId,
        config.sheetName,
        refToDelete.rowIndex + 1 // +1 to account for header row
      );

      // Clear cache to force refresh
      this.clearCache();

      console.log(`[ReferenceSheetManager] Successfully deleted reference: ${tag}`);

      return {
        success: true,
        affectedCount: 1,
        details: `Deleted reference: ${tag}`
      };

    } catch (error) {
      console.error('[ReferenceSheetManager] Failed to delete reference:', error);
      return {
        success: false,
        affectedCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Clear cache to force refresh on next fetch
   */
  public clearCache(): void {
    this.dataCache = null;
    this.cacheTimestamp = 0;
    console.log('[ReferenceSheetManager] Cache cleared');
  }

  /**
   * Check if references sheet is accessible
   */
  public async checkAccess(): Promise<{ hasAccess: boolean; error?: string }> {
    const config = this.getSpreadsheetConfig();
    if (!config) {
      return {
        hasAccess: false,
        error: 'Reference sheet not configured'
      };
    }

    try {
      const result = await googleSheetsService.checkSheetAccess(config.spreadsheetId);
      return {
        hasAccess: result.hasAccess && result.canWrite,
        error: result.error
      };
    } catch (error) {
      return {
        hasAccess: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
}

// Export singleton
export const referenceSheetManager = ReferenceSheetManagerService.getInstance();
